import {
  Heading,
  ListIndicator,
  ListItem,
  ListRoot,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { LuCircleCheck, LuCircleDashed } from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'

import type { useInvitedCommunities } from './useInvitedCommunities'

type Props = {
  invited: ReturnType<typeof useInvitedCommunities>
}

/**
 * Pending/accepted community invitations list, reused by the congratulations step
 * and the post-registration profile-selection screen. Presentational: all state is
 * owned by {@link useInvitedCommunities}.
 */
export const InvitedCommunitiesList = ({ invited }: Props) => {
  const { t } = useTranslation()
  const auth = useAuth()
  const {
    loading,
    edges,
    circlesCount,
    acceptedCommunities,
    handleAcceptCommunityInvitation,
  } = invited

  if (loading) {
    return <Spinner />
  }

  if (circlesCount === 0) {
    return null
  }

  return (
    <Stack>
      <Text
        textStyle={{ base: 'sm', md: 'md' }}
        fontWeight="medium"
        color="colorPalette.fg"
      >
        {t('register.wizard.congratulations.invitationsPrompt', {
          context: auth.user?.gender,
        })}
      </Text>
      {circlesCount > 1 && (
        <Heading>
          {t('register.wizard.congratulations.clickToJoinCommunities')}
        </Heading>
      )}

      <ListRoot gap="2" variant="plain" align="center">
        {edges.map((edge) => {
          const { slug, name } = edge.node!
          const isAccepted = acceptedCommunities.includes(slug)
          const invitation = edge.node?.invitations?.edges?.[0]?.node

          return (
            (invitation && (
              <ListItem
                key={slug}
                onClick={
                  isAccepted
                    ? undefined
                    : () => handleAcceptCommunityInvitation(slug, invitation.id)
                }
                cursor={isAccepted ? 'default' : 'pointer'}
                color={isAccepted ? 'gray.500' : 'inherit'}
                display="flex"
                alignItems="center"
                gap="2"
              >
                <ListIndicator asChild>
                  {isAccepted ? (
                    <LuCircleCheck color="yellow" />
                  ) : (
                    <LuCircleDashed />
                  )}
                </ListIndicator>
                {name}
                {isAccepted && (
                  <motion.span
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ marginLeft: '8px' }}
                  >
                    <Text color="green.500">
                      {t('register.wizard.congratulations.invitationAccepted')}
                    </Text>
                  </motion.span>
                )}
              </ListItem>
            )) || <div key={slug} />
          )
        })}
      </ListRoot>
    </Stack>
  )
}
